import { useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router'
import { ReverLoading } from '@itsrever/design-system'
import {
  useAvailableRestockLocations,
  useProcessRestocks,
  useProcessDetails,
  usePerformedReviews
} from '@/application/queries'
import { useToggleEcommerceSelector } from '@/application/toggle-ecommerce-selector'
import { ProcessDetails } from '@/ui/components/process-details'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { setSelectedEcommerce } from '@/redux/features/general-data/general-data-slice'
import { useProcessLogistics } from '@/hooks/use-process-logistics'

function ProcessDetailsPage() {
  const { t } = useTranslation()

  const { id } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()

  const ecommerceId = useAppSelector((s) => s.generalData.selectedEcommerce)
  const dispatch = useAppDispatch()

  useToggleEcommerceSelector()
  const { processDetails, loadingProcess } = useProcessDetails(id)
  const { restocks, loadingRestocks } = useProcessRestocks(id)

  // Settings, copies and restock locations read the store from the URL and the navbar, and a link
  // may carry none or the wrong one. A picked store follows the process; the group view stays.
  const processEcommerceId = processDetails?.ecommerceId
  useEffect(() => {
    if (!processEcommerceId) return
    if (ecommerceId && ecommerceId !== processEcommerceId)
      dispatch(setSelectedEcommerce(processEcommerceId))
    if (searchParams.get('ecommerce_id') === processEcommerceId) return
    setSearchParams(
      (prev) => {
        prev.set('ecommerce_id', processEcommerceId)
        return prev
      },
      { replace: true }
    )
  }, [processEcommerceId, ecommerceId, searchParams, setSearchParams, dispatch])

  const { data: logistics, isPending: loadingProcessLogistics } =
    useProcessLogistics({ processId: id })
  const { performedReviews, loadingPerformedReviews } = usePerformedReviews(id)
  const variantIds = processDetails?.returnedItems.map(
    (item) => item.product.variantId
  )

  const {
    restockLocations,
    loadingRestockLocations,
    status: restockLocationStatus
  } = useAvailableRestockLocations({
    variantIds: variantIds,
    orderId: processDetails?.orderId,
    ecommerceId: searchParams.get('ecommerce_id') ?? ecommerceId
  })

  if (
    loadingRestocks ||
    loadingProcessLogistics ||
    loadingProcess ||
    loadingPerformedReviews ||
    loadingRestockLocations
  )
    return (
      <div className="flex size-full items-center justify-center">
        <ReverLoading />
      </div>
    )

  if (restockLocationStatus === 'error') {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-neutral-500">
          {t('process_details_page.error_loading')}
        </p>
      </div>
    )
  }

  return (
    <ProcessDetails
      processDetails={processDetails}
      processLogistics={logistics?.logistics_summary}
      restocks={restocks}
      reviews={performedReviews}
      restockLocations={restockLocations}
    />
  )
}

export default ProcessDetailsPage
